<template>
  <a-button
    :type="type"
    :size="size"
    :danger="danger"
    :ghost="ghost"
    :loading="loading"
    :disabled="disabled"
    :block="block"
    :html-type="htmlType"
    :shape="shape"
    :href="href"
    :target="target"
    :class="buttonClass"
    @click="onClick"
  >
    <template v-if="icon && !loading" #icon>
      <i :class="icon" aria-hidden="true" />
    </template>

    <slot>
      <span v-if="displayLabel" class="h-button__label">{{ displayLabel }}</span>
    </slot>

    <i
      v-if="iconRight && !loading"
      class="h-button__icon-right"
      :class="iconRight"
      aria-hidden="true"
    />
  </a-button>
</template>

<script lang="ts" setup>
import { computed, useSlots } from "vue";
import { useI18n } from "vue-i18n";
import type { SizeType } from "ant-design-vue/es/config-provider";

type ButtonType = "primary" | "default" | "dashed" | "link" | "text";
type ButtonShape = "default" | "circle" | "round";
type HtmlType = "button" | "submit" | "reset";

interface Props {
  /** Text hiển thị. Có thể truyền sẵn $t(...) hoặc key nếu bật i18n */
  label?: string;
  /** true → gọi $t(label) */
  i18n?: boolean;
  icon?: string;
  iconRight?: string;
  type?: ButtonType;
  size?: SizeType;
  danger?: boolean;
  ghost?: boolean;
  loading?: boolean;
  disabled?: boolean;
  block?: boolean;
  htmlType?: HtmlType;
  shape?: ButtonShape;
  href?: string;
  target?: string;
  /** Class bổ sung */
  classBtn?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
  i18n: false,
  icon: "",
  iconRight: "",
  type: "default",
  size: "middle",
  danger: false,
  ghost: false,
  loading: false,
  disabled: false,
  block: false,
  htmlType: "button",
  shape: "default",
  href: undefined,
  target: undefined,
  classBtn: "",
});

const emit = defineEmits<{
  (e: "click", event: MouseEvent): void;
}>();

const slots = useSlots();
const { t } = useI18n();

const displayLabel = computed(() => {
  if (!props.label) return "";
  return props.i18n ? t(props.label) : props.label;
});

const isIconOnly = computed(() => {
  const hasLabel = !!displayLabel.value || !!slots.default;
  return !!props.icon && !hasLabel && !props.iconRight;
});

const buttonClass = computed(() => [
  "h-button",
  props.classBtn,
  {
    "h-button--icon-only": isIconOnly.value,
    "h-button--with-right-icon": !!props.iconRight,
  },
]);

const onClick = (event: MouseEvent) => {
  if (props.disabled || props.loading) return;
  emit("click", event);
};
</script>

<style scoped>
.h-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  border-radius: 8px;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease,
    box-shadow 0.2s ease, opacity 0.2s ease;
}

.h-button :deep(.anticon),
.h-button :deep(i) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.h-button__label {
  line-height: 1.2;
}

.h-button__icon-right {
  margin-left: 2px;
  font-size: 0.92em;
}

/* Icon-only: cân đối kích thước click */
.h-button--icon-only {
  width: 36px;
  min-width: 36px;
  padding-inline: 0;
}

.h-button--icon-only.ant-btn-sm {
  width: 28px;
  min-width: 28px;
}

.h-button--icon-only.ant-btn-lg {
  width: 44px;
  min-width: 44px;
}

/* Primary — Airbnb Rausch */
.h-button.ant-btn-primary:not(.ant-btn-dangerous):not(.ant-btn-background-ghost) {
  background: var(--brand-rausch, #FF385C);
  border-color: var(--brand-rausch, #FF385C);
  box-shadow: none;
}

.h-button.ant-btn-primary:not(.ant-btn-dangerous):not(.ant-btn-background-ghost):hover {
  background: var(--brand-rausch-hover, #E31C5F);
  border-color: var(--brand-rausch-hover, #E31C5F);
}

.h-button.ant-btn-primary:not(.ant-btn-dangerous):not(.ant-btn-background-ghost):active {
  background: var(--brand-rausch-active, #D70466);
  border-color: var(--brand-rausch-active, #D70466);
}

.h-button.ant-btn-primary:not(.ant-btn-dangerous):focus-visible {
  outline: 2px solid rgba(255, 56, 92, 0.35);
  outline-offset: 2px;
}

.h-button:disabled,
.h-button.ant-btn-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>

<!--
  Hướng dẫn sử dụng — h-button (Primary)

  Global:
    <h-button label="Lưu" icon="fas fa-save" @click="onSave" />
    <h-button label="text.goBack" i18n icon="fa-solid fa-angle-left" type="default" />
    <h-button icon="fas fa-plus" />  <!-- icon-only
  Import:
    import Primary from '@/components/ant/button/Primary.vue'
    <h-button ... />
  Props:
    label, i18n, icon, iconRight, type (default: default), size, danger, ghost, loading,
    disabled, block, htmlType, shape, href, target, classBtn
  Events: click
-->
